import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from 'react-i18next';

import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';

import { useUpdateDeviceMutation } from '../../../queries';
import { useGetLocations, useCreateLocation, countryTzList, tzLabel } from '../../../queries/locations';
import { getFormErrorMessage } from '../../../config/global';
import { toggleDialog } from '../../../redux/Slices/Global/globalSlice';

const IcEdit = () => <svg viewBox="0 0 24 24"><path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3z" /></svg>;
const IcClose = () => <svg width="18" height="18" viewBox="0 0 24 24" style={{ stroke: 'currentColor', fill: 'none', strokeWidth: 2 }}><path d="M6 6l12 12M18 6L6 18" /></svg>;

// The device edit dialog is intentionally minimal: Name, Status, Branch/Location.
// The Branch picker is the single source of a device's physical location + timezone
// (Device::resolvedTimezone). The old Country/City manual geo-picker was removed —
// it relied on a broken GeoNames API and the geo-IP it fed back detected the relay
// ("Kansas City"), not the venue. Branch supersedes it.
function Update({ device }) {
    const { t } = useTranslation();
    const { openDialog } = useSelector((state) => state.global);
    const { formState: { errors }, control, handleSubmit, reset } = useForm();

    const status = [
        { name: t('devices.status_active'), code: 'ACTIVE' },
        { name: t('devices.status_inactive'), code: 'INACTIVE' },
    ];

    const dispatch = useDispatch();
    const updateDevice = useUpdateDeviceMutation();

    // Branch/Location assignment — drives the device's scheduling timezone + displayed location.
    const locationsRaw = useGetLocations().data;
    const branches = Array.isArray(locationsRaw) ? locationsRaw : (locationsRaw?.data ?? []);
    const [locationId, setLocationId] = useState(device.location_id ?? null);
    const branchOptions = branches.map((l) => ({
        label: l.timezone ? `${l.name} · ${l.timezone}` : l.name,
        value: l.id,
    }));

    // Inline "Create Branch" shortcut — create a branch without leaving the dialog.
    const createLocation = useCreateLocation();
    const [showCreate, setShowCreate] = useState(false);
    const [newBranch, setNewBranch] = useState({ name: '', country: '', timezone: 'Asia/Riyadh' });
    const countryOptions = countryTzList.map((c) => ({ label: c.label, value: c.label, timezone: c.timezone }));

    const onPickCountry = (label) => {
        const match = countryTzList.find((c) => c.label === label);
        setNewBranch((b) => ({ ...b, country: label, timezone: match ? match.timezone : b.timezone }));
    };

    const saveBranch = async () => {
        if (!newBranch.name.trim()) return;
        const res = await createLocation.mutateAsync({
            name: newBranch.name.trim(), country: newBranch.country, timezone: newBranch.timezone,
        });
        const created = res?.data?.data?.location ?? res?.data?.location;
        if (created?.id) setLocationId(created.id);
        setShowCreate(false);
        setNewBranch({ name: '', country: '', timezone: 'Asia/Riyadh' });
    };

    useEffect(() => {
        reset({ name: device.name, status: device.status });
        setLocationId(device.location_id ?? null);
    }, [device]);

    const onSubmit = async (formData) => {
        await updateDevice.mutateAsync({
            id: device.id,
            data: {
                name: formData.name,
                status: formData.status,
                location_id: locationId,
            },
        });
    };

    const onClose = () => dispatch(toggleDialog("updateDevice"));

    return (
        <Dialog visible={openDialog.updateDevice}
            style={{ width: '460px' }}
            showHeader={false}
            modal className="pp-dlg"
            breakpoints={{ '960px': '95vw' }}
            onHide={onClose}>

            <div className="pp-dlg-hd">
                <span className="ic"><IcEdit /></span>
                <div><div className="t">{t('devices.group_update_device_status')}</div></div>
                <span className="x" onClick={onClose}><IcClose /></span>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="pp-dlg-bd">

                    <div className="pp-dlg-fld">
                        <label>{t('form.name')}</label>
                        <Controller name="name" control={control}
                            rules={{ required: t('devices.group_name_required') }}
                            render={({ field, fieldState }) => (
                                <InputText id={field.name} {...field} autoFocus inputRef={field.ref}
                                    className={classNames({ 'p-invalid': fieldState.invalid })}
                                />
                            )} />
                        {getFormErrorMessage('name', errors)}
                    </div>

                    <div className="pp-dlg-fld">
                        <label htmlFor="basic-default-company">{t('table.status')}</label>
                        <Controller name="status" control={control}
                            rules={{ required: t('devices.group_status_required') }}
                            render={({ field, fieldState }) => (
                                <Dropdown id={field.name} {...field}
                                    onChange={(e) => field.onChange(e.value)}
                                    options={status}
                                    optionLabel="name"
                                    optionValue="code"
                                    inputRef={field.ref}
                                    className={classNames({ 'p-invalid': fieldState.invalid })}
                                />
                            )} />
                        {getFormErrorMessage('status', errors)}
                    </div>

                    <div className="pp-dlg-fld">
                        <label>{t('devices.location_label')}</label>
                        <Dropdown
                            value={locationId}
                            options={branchOptions}
                            onChange={(e) => setLocationId(e.value)}
                            placeholder={branchOptions.length ? t('devices.location_placeholder') : t('devices.location_none')}
                            emptyMessage={t('devices.location_none')}
                            disabled={branchOptions.length === 0}
                            showClear
                            filter
                        />
                        <small className="text-muted">{t('devices.location_hint')}</small>

                        {/* Inline create-branch shortcut */}
                        {!showCreate ? (
                            <Button type="button" link className="p-0 mt-2" icon="pi pi-plus"
                                label={t('devices.location_create')}
                                onClick={() => setShowCreate(true)} />
                        ) : (
                            <div className="mt-2 p-3" style={{ background: 'var(--pp-comp-surface-2)', borderRadius: 8 }}>
                                <label className="form-label mb-1">{t('devices.location_create_name')}</label>
                                <InputText className="w-100 mb-2" value={newBranch.name}
                                    onChange={(e) => setNewBranch((b) => ({ ...b, name: e.target.value }))}
                                    placeholder={t('devices.location_create_name_ph')} />

                                <label className="form-label mb-1">{t('form.country')}</label>
                                <Dropdown className="w-100 mb-2" value={newBranch.country} options={countryOptions}
                                    onChange={(e) => onPickCountry(e.value)} filter
                                    placeholder={t('devices.location_create_country_ph')} />

                                <label className="form-label mb-1">{t('video_wall.locations.timezone', 'Timezone')}</label>
                                <div className="mb-2"><small className="text-muted"><i className="pi pi-clock me-1" />{tzLabel(newBranch.timezone)}</small></div>

                                <div className="d-flex justify-content-end gap-2">
                                    <Button type="button" text size="small" label={t('buttons.cancel')} onClick={() => setShowCreate(false)} />
                                    <Button type="button" size="small" icon="pi pi-check"
                                        label={t('buttons.save')} loading={createLocation.isLoading}
                                        disabled={!newBranch.name.trim()} onClick={saveBranch} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="pp-dlg-ft">
                    <button type="submit" className="btn primary" disabled={updateDevice.isLoading}>{t('buttons.update')}</button>
                    <button type="button" className="btn ghost" disabled={updateDevice.isLoading} onClick={onClose}>{t('buttons.cancel')}</button>
                </div>
            </form>
        </Dialog>
    );
}

export default Update;
